import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Apple, Sparkles, Flame, Percent } from "lucide-react";
import { LogoMark } from "./Logo";

const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

const ATTRS = [
  [Sparkles, "Lightly Sparkling"],
  [Apple, "Fruity & Medium Sweet"],
  [Percent, "<5% ABV"],
  [Flame, "Perfect with Spicy Food"],
];

const PANELS = [
  {
    tag: "Panel A",
    title: "Crafted in the Himalayas",
    body: (
      <>
        <p className="mt-5 max-w-md text-base leading-relaxed text-forest-deep">Made in India using handpicked Himalayan apples, grown in the pristine orchards of Himachal Pradesh, and pure mountain water. The seal says it all: crafted in the Himalayas, India.</p>
        <div className="mt-7 flex items-center gap-4"><LogoMark className="h-12 w-12" /><span className="font-mono text-[12px] uppercase tracking-[0.22em] text-forest">Himalayan peaks artwork · embossed</span></div>
      </>
    ),
  },
  {
    tag: "Panel B",
    title: "What's in the can",
    body: (
      <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ATTRS.map(([Icon, label]) => (
          <li key={label} className="flex items-center gap-3 border-t border-forest-deep/20 pt-3 text-base text-forest-deep">
            <Icon className="h-5 w-5 shrink-0 text-forest" />{label}
          </li>
        ))}
      </ul>
    ),
  },
  {
    tag: "Panel C",
    title: <>We don't say cheers, <span className="italic text-forest">we say JAI <span className="font-deva not-italic">हो!</span></span></>,
    body: <p className="mt-5 max-w-md text-base leading-relaxed text-forest-deep">While others raise a glass with Cheers, Salud, Prost, Santé, we raise ours with JAI <span className="font-deva">हो!</span> That's the Himachal <span className="font-deva">हो!</span> way.</p>,
  },
];

export default function Anatomy() {
  const ref = useRef(null);
  const stage = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const s = Math.min(Math.max(p, 0) * 3, 3);
    const k = Math.floor(s);
    const rot = ((Math.PI * 2) / 3) * (k + (k < 3 ? smooth(0.55, 1, s - k) : 0));
    if (stage.current) stage.current.dataset.rot = String(rot);
    setActive(Math.min(k, 2));
  });

  return (
    <section id="craft" ref={ref} data-orbit className="relative h-[330vh] bg-mist text-forest-deep" data-testid="anatomy-section">
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(40%_50%_at_50%_45%,rgba(255,255,255,.75),transparent_70%)]" />
        <p className="eyebrow absolute left-5 top-24 text-forest sm:left-8 lg:left-12">Chapter 01 — Anatomy of a can</p>

        <div
          ref={stage}
          data-can-stage
          data-rot="0"
          data-variant="original"
          data-snap="1"
          data-testid="anatomy-can-stage"
          className={`absolute left-0 top-[16svh] h-[30svh] w-full transition-[left] duration-[1400ms] ease-[cubic-bezier(.22,1,.36,1)] lg:top-[10vh] lg:h-[80vh] lg:w-[44%] ${active === 1 ? "lg:left-[6%]" : "lg:left-[50%]"}`}
        />

        {PANELS.map((p, i) => (
          <motion.div
            key={p.tag}
            data-testid={`anatomy-panel-${i}`}
            animate={{ opacity: active === i ? 1 : 0, y: active === i ? 0 : 28 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            style={{ pointerEvents: active === i ? "auto" : "none" }}
            className={`absolute inset-x-5 bottom-[9svh] sm:inset-x-8 lg:bottom-auto lg:top-1/2 lg:w-[40%] lg:-translate-y-1/2 ${i === 1 ? "lg:left-[54%]" : "lg:left-12"}`}
          >
            <p className="font-mono text-[12px] uppercase tracking-[0.24em] text-forest-deep/80">{p.tag} · 0{i + 1} / 03</p>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.02] text-forest-deep sm:text-5xl lg:text-6xl">{p.title}</h2>
            {p.body}
          </motion.div>
        ))}

        <div className="absolute bottom-6 left-5 right-5 flex items-center gap-4 sm:left-8 lg:left-12 lg:right-auto lg:w-[320px]">
          {[0, 1, 2].map((i) => (
            <span key={i} className="relative h-[2px] flex-1 overflow-hidden bg-forest-deep/15">
              <span className={`absolute inset-0 origin-left bg-forest transition-transform duration-700 ${active >= i ? "scale-x-100" : "scale-x-0"}`} />
            </span>
          ))}
          <span className="font-mono text-[12px] text-forest-deep/80">0{active + 1}/03</span>
        </div>
      </div>
    </section>
  );
}
